import type { PropsWithChildren, ReactNode } from "react";
import { IconInfoOutline } from "@humansignal/icons";
import { Tooltip } from "../Tooltip/Tooltip";
import styles from "./settings-card.module.css";

/**
 * SettingsCard — the canonical "titled container with optional description
 * and form content" used across Platform Settings, Org Settings, Auto-
 * suspend rules, Notifications, and every chart / form grouping in the
 * app's admin surfaces.
 *
 * Replaces a family of bespoke classes (`form-card`, `rule-card`,
 * `chart-card`, etc.) that had drifted apart on padding / radius /
 * title-size. Everything here is driven by platform tokens so a future
 * tweak lands in one place.
 *
 * Composable rather than monolithic: pass a ``title`` + ``description``
 * for the common case, or drop whatever header markup you need into
 * ``header``. ``footer`` is for in-card Save / Discard rows.
 */
type SettingsCardProps = PropsWithChildren<{
  /** Short title shown at the top of the card. */
  title?: ReactNode;
  /** Supporting copy under the title. Kept short — this is not body text. */
  description?: ReactNode;
  /** Bypass the title/description slot and render arbitrary header markup. */
  header?: ReactNode;
  /** Trailing actions slot inside the header (e.g. a toggle). */
  headerActions?: ReactNode;
  /** In-card footer — typically Save / Discard buttons. */
  footer?: ReactNode;
  /** Remove inner padding so the caller controls it (useful for charts). */
  flush?: boolean;
  /** Tighter density — good for charts + stat panels that sit in a grid. */
  compact?: boolean;
  /** Negative/destructive tone — red border + red title, same shape as a
   * normal card. For Danger-Zone style actions. */
  danger?: boolean;
  className?: string;
}>;

export const SettingsCard = ({
  title,
  description,
  header,
  headerActions,
  footer,
  flush = false,
  compact = false,
  danger = false,
  className,
  children,
}: SettingsCardProps) => {
  const rootClass = [styles.card, compact ? styles.compact : "", danger ? styles.danger : "", className ?? ""]
    .filter(Boolean)
    .join(" ");
  const contentClass = [styles.content, flush ? styles.contentFlush : ""].filter(Boolean).join(" ");
  const hasHeader = header || title || description || headerActions;

  return (
    <div className={rootClass}>
      {hasHeader && (
        <div className={styles.header}>
          {header ?? (
            <div className={styles.headerText}>
              {title ? (
                <h3 className={styles.title}>
                  {title}
                  {description && (
                    <Tooltip title={<div className={styles.descTip}>{description}</div>}>
                      <button type="button" className={styles.infoBtn} aria-label="More information">
                        <IconInfoOutline />
                      </button>
                    </Tooltip>
                  )}
                </h3>
              ) : (
                // No title to anchor the (i) on — fall back to inline copy.
                description && <p className={styles.description}>{description}</p>
              )}
            </div>
          )}
          {headerActions && <div className={styles.headerActions}>{headerActions}</div>}
        </div>
      )}
      <div className={contentClass}>{children}</div>
      {footer && <div className={styles.footer}>{footer}</div>}
    </div>
  );
};
